/**
 * VortexLogo - Theme-aware logo component
 * Sprint 34.1: Branding & Graphics Integration
 * 
 * Automatically switches between dark/light logo variants
 * based on the current theme from themeStore.
 */

import { useThemeStore } from '../../stores/themeStore';

// Import static logos
import logoStaticDark from '../../assets/branding/logo-static-dark.svg';
import logoStaticLight from '../../assets/branding/logo-static-light.svg';
import logoAnimatedDark from '../../assets/branding/logo-animated-dark.svg';
import logoAnimatedLight from '../../assets/branding/logo-animated-light.svg';
import iconStaticDark from '../../assets/branding/icon-static-dark.svg';
import iconStaticLight from '../../assets/branding/icon-static-light.svg';
import iconAnimatedDark from '../../assets/branding/icon-animated-dark.svg';
import iconAnimatedLight from '../../assets/branding/icon-animated-light.svg';

interface LogoProps {
  /** Width of the logo (height auto-scales to maintain aspect ratio) */
  width?: number | string;
  /** Height of the logo (if set, overrides width-based sizing) */
  height?: number | string;
  /** Additional CSS class names */
  className?: string;
  /** Alt text for accessibility */
  alt?: string;
  /** Inline style overrides */
  style?: React.CSSProperties;
}

/**
 * VortexLogo - Static logo, theme-aware
 * Use for navigation headers, login page branding, etc.
 */
export function VortexLogo({
  width = 180,
  height,
  className = '',
  alt = 'VortexCA Logo',
  style,
}: LogoProps) {
  const resolved = useThemeStore((state) => state.resolved);
  const logoSrc = resolved === 'dark' ? logoStaticDark : logoStaticLight;

  return (
    <img
      src={logoSrc}
      alt={alt}
      width={width}
      height={height}
      className={`vortex-logo ${className}`}
      style={style}
    />
  );
}

/**
 * VortexLogoAnimated - Animated logo, theme-aware
 * Use for loading screens, session transitions, etc.
 */
export function VortexLogoAnimated({
  width = 180,
  height,
  className = '',
  alt = 'VortexCA Loading',
  style,
}: LogoProps) {
  const resolved = useThemeStore((state) => state.resolved);
  const logoSrc = resolved === 'dark' ? logoAnimatedDark : logoAnimatedLight;

  return (
    <img
      src={logoSrc}
      alt={alt}
      width={width}
      height={height}
      className={`vortex-logo vortex-logo-animated ${className}`}
      style={style}
    />
  );
}

/**
 * VortexIcon - Static icon (compact), theme-aware
 * Use for favicons, small spaces, mobile nav, etc.
 */
export function VortexIcon({
  width = 32,
  height,
  className = '',
  alt = 'VortexCA',
  style,
}: LogoProps) {
  const resolved = useThemeStore((state) => state.resolved);
  const iconSrc = resolved === 'dark' ? iconStaticDark : iconStaticLight;

  return (
    <img
      src={iconSrc}
      alt={alt}
      width={width}
      height={height}
      className={`vortex-icon ${className}`}
      style={style}
    />
  );
}

/**
 * VortexIconAnimated - Animated icon (compact), theme-aware
 * Use for inline loading spinners, status indicators, etc.
 */
export function VortexIconAnimated({
  width = 32,
  height,
  className = '',
  alt = 'Loading',
  style,
}: LogoProps) {
  const resolved = useThemeStore((state) => state.resolved);
  const iconSrc = resolved === 'dark' ? iconAnimatedDark : iconAnimatedLight;

  return (
    <img
      src={iconSrc}
      alt={alt}
      width={width}
      height={height}
      className={`vortex-icon vortex-icon-animated ${className}`}
      style={style}
    />
  );
}
